import React from 'react';
import classnames from 'classnames';
import { commonEnums, cmxDateTime } from '@codametrix/ui-common';
import { Accordion } from '@codametrix/ui-components';
import { DMNTable } from './dmn-table';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import './styles/index.scss';
const { TimelineStatus } = commonEnums;

type ProcessTimelineProps = React.HTMLAttributes<HTMLDivElement> & {
  stages: CMxCommonApp.TimelineStage[];
  stifleSelection?: boolean;
  clickObserver?: (stageId: string) => void;
  doubleClickObserver?: (stageId: string) => void;
};

const noOp = () => {};
const DefaultProps = {
  clickObserver: noOp,
  doubleClick: noOp
};

const getStatusIcon = (status: string) => {
  if (status === TimelineStatus.STARTED) {
    return (
      <BiomeIcon
        iconName={IconName.Location}
        iconSize="16"
        titleText="Started Process"
      />
    );
  } else if (status === TimelineStatus.COMPLETE) {
    return (
      <BiomeIcon
        iconName={IconName.Check_Circle}
        iconSize="16"
        titleText="Process Complete"
      />
    );
  } else if (status === TimelineStatus.IN_PROGRESS) {
    return (
      <BiomeIcon
        iconName={IconName.Pending}
        iconSize="16"
        titleText="In Progress"
      />
    );
  }
};

const idForEvent = (ev: React.MouseEvent) => {
  //@ts-ignore
  return ev.target?.dataset.id ?? null;
};

class ProcessTimeline extends React.Component<ProcessTimelineProps> {
  accordionRef: React.RefObject<Accordion>;
  containerRef: React.RefObject<HTMLDivElement>;

  static displayName = 'ProcessTimeline';
  static defaultProps = {
    stifleSelection: false,
    clickObserver: noOp,
    doubleClick: noOp
  };

  constructor(props: ProcessTimelineProps) {
    super(props);
    this.accordionRef = React.createRef();
    this.containerRef = React.createRef();
    this.handleClick = this.handleClick.bind(this);
    this.state = {};
  }

  handleClick(ev: React.MouseEvent) {
    ev.preventDefault();
    const itemId = idForEvent(ev);
    const { accordionRef } = this;

    if (itemId !== null && accordionRef.current !== null) {
      document.querySelectorAll('.accordion-section.highlight').forEach(el => {
        el.classList.remove('highlight');
      });

      const el = document
        .getElementById(`stage-${itemId}`)
        ?.closest('.accordion-section');

      if (el) {
        el.classList.add('highlight');
        this.props.clickObserver(itemId);
        setTimeout(() => {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }

  handleDoubleClick(ev: React.MouseEvent) {
    const itemId = idForEvent(ev);
    const { accordionRef } = this;

    if (itemId !== null && accordionRef.current !== null) {
      accordionRef.current?.toggle(itemId);
      if (!this.state[itemId]) {
        this.handleClick(ev);
      }
      const s = { ...this.state };
      s[itemId] = !s[itemId];
      this.setState(s);
    }
  }

  render() {
    const props = { ...this.props };
    const { stages } = props;

    const unselectableAccordion = props.stifleSelection ?? false;

    let lastUpdatedDate = '';

    const lst = stages.map((stage, idx) => {
      const updateDate =
        stage.displayDate && typeof stage.displayDate === 'string'
          ? cmxDateTime.format(stage.displayDate, cmxDateTime.FORMATS.DATE)
          : 'N/A';
      let displayDate;

      if (idx === 0) {
        displayDate = updateDate;
      } else {
        displayDate = updateDate === lastUpdatedDate ? '' : updateDate;
      }
      lastUpdatedDate = updateDate;

      const timeFormat =
        idx === 0
          ? cmxDateTime.FORMATS.TIME_WITH_ZONE
          : cmxDateTime.FORMATS.TIME;

      const updateTime =
        stage.displayDate && typeof stage.displayDate === 'string'
          ? cmxDateTime.format(stage.displayDate, timeFormat)
          : 'N/A';

      const peeked = stage.steps[stage.steps.length - 1] ?? false;
      const statusClass = `status-${stage.status?.toLowerCase()}`;
      const depthClass = `depth-${stage.depth}`;

      const header = (
        <div
          id={`stage-${stage.key}`}
          key={stage.key}
          className={`timeline-item ${statusClass} ${depthClass}`}>
          <div className="timeline-meta-col">
            <div className={`timeline-status ${statusClass}`}>
              {getStatusIcon(`${stage.status}`)}
            </div>
            <div className="timeline-datetime">
              <span className="display-datetime">
                <span className="display-date">{displayDate}</span>
                <span className="display-time">{updateTime}</span>
              </span>
            </div>
          </div>

          <div className="timeline-title timeline-description-col">
            <span className="stage-title">{stage.title}</span>
            <span className="stage-last-step">
              {peeked && peeked.activity.activityDefinition.name}
            </span>{' '}
            <span className="stage-parent">
              Started by: {stage.parent?.title}
            </span>
            <span className="debug-information">{stage.key}</span>
          </div>
        </div>
      );

      const steps = [...stage.steps].reverse();
      const stepItems = steps.map(step => {
        const eventType = step.activity?.activityDefinition?.type ?? 'DNE';

        if (
          eventType === 'startEvent' ||
          eventType === 'conditionalStartEvent'
        ) {
          return false;
        }

        const { activity } = step;
        const { activityDefinition } = activity;

        const startDate = activity.createdDate;
        const updateDate =
          activity.lastEndTimestamp || activity.lastStartTimestamp;

        const updateDateMoment = startDate
          ? cmxDateTime.format(
              startDate,
              cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE
            )
          : 'N/A';

        // calculate total duration
        const duration = cmxDateTime.calculateDuration(startDate, updateDate);
        const seconds = duration.asSeconds() % 60;
        const durationInSeconds = Math.max(seconds, 0);
        const formattedNumber = parseFloat(`${durationInSeconds}`).toFixed(2);

        const name = activityDefinition?.name ?? activityDefinition.activityId;
        const callsSub = activityDefinition.type === 'callActivity'; // && stepSet.has(activityDefinition.subprocessName as string);

        const displayName = callsSub ? (
          <a href="#/" data-id={activityDefinition.subprocessName}>
            <span className="step-name">{name}</span>
            &#8673;{'  '}
            <span className="debug-information">
              {activityDefinition.subprocessName}
            </span>
          </a>
        ) : (
          <span>
            <span className="step-name">{name}</span>{' '}
            <span className="debug-information">
              {activityDefinition.activityId}
            </span>
          </span>
        );
        const hasRules = step.decisions.length > 0;
        const classes = classnames('timeline-step', { 'has-rules': hasRules });

        return (
          <li key={activityDefinition.activityId} className={classes}>
            {displayName}{' '}
            <div className="rules">
              {/* <b>Rules</b> */}
              <DMNTable step={step} />
            </div>
            <span className="duration-readout">
              {formattedNumber} seconds - {updateDateMoment}
            </span>
          </li>
        );
      });

      const body = (
        <div className="timeline-body-item  timeline-description-col">
          <div className="timeline-meta-col"></div>
          <div className="timeline-title timeline-ordered-list">
            <ol reversed>{stepItems}</ol>
          </div>
        </div>
      );

      return {
        id: stage.key,
        data: stage,
        header,
        body
      };
    });

    return (
      <div
        className="process-timeline"
        ref={this.containerRef}
        id="processTimeline"
        data-testid="process-timeline"
        onDoubleClick={this.handleDoubleClick}
        onClick={this.handleClick}>
        <Accordion
          ref={this.accordionRef}
          selectable={!unselectableAccordion}
          items={lst}
        />
      </div>
    );
  }
}

export { ProcessTimeline };
